import { FloorPlanFilterListType } from '@/components/floor-plan-filter/floor-plan-filter-list-type.type';
import { FilterTile } from '@/components/floor-plan-map/floor-plan-map.interface';
import { IMeetingPlacePageFloorPlan } from '@/lib/fragments/meetingPlacePageFloorPlan';
import { IMeetingPlacePageServiceItem } from '@/lib/fragments/meetingPlacePageServiceItem';
import { TTenantTypes } from '@/lib/fragments/meetingPlacePageTenantList';
import { FloorPlanCategoriesData } from '@/templates/meeting-place/page/floorplan/meeting-place-page-floorplan';
import { generateCorrectSlugForPage } from '../generate-correct-slug';
import {
  DictionarySubDirectories,
  getUrlSubdirectory,
} from '../rendered-page-creation/get-url-subdirectories';

export interface CategoryCount {
  [category: string]: number;
}

type Lists = {
  [key in FloorPlanFilterListType]?:
  | TTenantTypes[]
  | IMeetingPlacePageServiceItem[];
};

export const getInitialFilterTiles = (
  floorPlanCategoriesData: FloorPlanCategoriesData,
  lists: Lists,
  floorPlanPage: IMeetingPlacePageFloorPlan,
  config: any,
) => {
  const initialFilterTiles: FilterTile[] = [
    {
      title: floorPlanPage?.shopCategoryName,
      type: 'shop',
      images: { coverImage: floorPlanCategoriesData?.shop?.image },
      floorPlanIDs: [],
      unitId: [],
    },
    {
      title: floorPlanPage?.foodBeverageCatergoryName,
      type: 'fb',
      images: { coverImage: floorPlanCategoriesData?.fb?.image },
      floorPlanIDs: [],
      unitId: [],
    },
    {
      title: floorPlanPage?.entertainmentCategoryName,
      type: 'entertainment',
      images: { coverImage: floorPlanCategoriesData?.entertainment?.image },
      floorPlanIDs: [],
      unitId: [],
    },
    {
      title: floorPlanPage?.servicesCategoryName,
      type: 'service',
      images: { coverImage: floorPlanCategoriesData?.service?.image },
      floorPlanIDs: [],
    },
  ];

  const filteredTiles = initialFilterTiles.filter((tile) => {
    const isIncluded = lists[tile.type];
    return isIncluded && isIncluded.length > 0;
  });

  filteredTiles.forEach((tile) => {
    const isIncluded = lists[tile.type];
    if (isIncluded) {
      let categories: CategoryCount;
      if (tile.type === 'service') {
        categories = getCategoryInformationForServices(
          isIncluded as IMeetingPlacePageServiceItem[],
          config,
        );
      } else {
        categories = getCategoryInformationForTenants(
          isIncluded as TTenantTypes[],
        );
      }
      if (Object.keys(categories).length === 0) {
        tile.shouldSkipCategories = true;
      }
    }
  });

  return filteredTiles;
};

export const filterTenantByCategory = (
  tenant: TTenantTypes,
  category: string,
): boolean => {
  const tenantCategories = getCategoryInformationForTenants([tenant]);
  return tenantCategories[category] !== undefined;
};

export const filterServiceByCategory = (
  service: IMeetingPlacePageServiceItem,
  category: string,
  config: any,
): boolean => {
  const shopCategories = getCategoryInformationForServices([service], config);
  return shopCategories[category] !== undefined;
};

export const getAllTilesList = (
  config: any,
  urlSubdirectories: DictionarySubDirectories[],
  shops?: TTenantTypes[],
  foodbeverage?: TTenantTypes[],
  entertainment?: TTenantTypes[],
  services?: IMeetingPlacePageServiceItem[],
): FilterTile[] => {
  const prefix = config?.GATSBY_SITE_PATH_PREFIX;
  const isNewServiceDesignEnabled =
    config?.NEXT_PUBLIC_SHOW_NEW_SERVICE_DESIGN === 'true';
  const tiles: FilterTile[] = [];

  const tenants: TTenantTypes[] = [];
  if (shops) tenants.push(...shops);
  if (foodbeverage) tenants.push(...foodbeverage);
  if (entertainment) tenants.push(...entertainment);

  for (const tenant of tenants) {
    const urlSubDirectory = getUrlSubdirectory(
      urlSubdirectories,
      tenant?.sys?.locale,
      tenant?.__typename,
    );
    const contentfulId = tenant.sys.id;

    const tenantSlug = tenant.slug ? tenant.slug : '';

    const slug = generateCorrectSlugForPage(
      {
        slug: tenantSlug,
        customizedSlug: tenant.customizedSlug,
        node_locale: tenant.sys.locale,
      },
      prefix,
      urlSubDirectory,
    );

    const title = tenant.name
      ? tenant.name
      : tenant.brand?.name
        ? tenant.brand?.name
        : '';

    const logo = tenant.logo
      ? tenant.logo
      : tenant.brand?.logo
        ? tenant.brand?.logo
        : undefined;

    const coverImage = tenant.coverImage
      ? tenant.coverImage
      : tenant.brand?.coverImage
        ? tenant.brand?.coverImage
        : undefined;

    tiles.push({
      contentfulId,
      floorPlanIDs: tenant?.floorPlanIDs?.map((id) => id?.trim()),
      unitId: tenant?.unitId?.map((id) => id?.split("-").slice(1).join("-").trim()),
      categories: getCategoryInformationForTenants([tenant]),
      images: { logo, coverImage },
      slug,
      title,
      type: tenant.__typename as FloorPlanFilterListType,
    });
  }

  for (const service of services ?? []) {
    const urlSubDirectory = getUrlSubdirectory(
      urlSubdirectories,
      service?.sys?.locale,
      service?.__typename,
    );
    const title = service.name
      ? service.name
      : service.serviceType?.name
        ? service.serviceType?.name
        : '';

    const slug = generateCorrectSlugForPage(
      {
        slug: isNewServiceDesignEnabled
          ? service.serviceType?.slug
          : service.slug,
        customizedSlug: isNewServiceDesignEnabled
          ? service.serviceType.customizedSlug
          : service.customizedSlug,
        node_locale: service?.sys?.locale,
      },
      prefix,
      urlSubDirectory,
    );

    tiles.push({
      contentfulId: service?.sys?.id?.toLocaleLowerCase(),
      categories: getCategoryInformationForServices([service], config),
      floorPlanIDs: service.floorPlanIDs.map((id) => id.trim()),
      images: {
        coverImage:
          service?.mediaField || service.serviceType?.coverImageWrapper,
        icon: service?.floorPlanIcon || service.serviceType?.floorPlanIcon,
      },
      slug,
      title,
      type: service.__typename as FloorPlanFilterListType,
    });
  }

  return tiles;
};

export const getCategoryInformationForServices = (
  list: IMeetingPlacePageServiceItem[] | undefined,
  config: any,
): CategoryCount => {
  const categoryCount: CategoryCount = {};

  if (!list) return categoryCount;
  const categoryList: string[] = list.map((service) => {
    const isNewServiceDesignEnabled =
      config?.NEXT_PUBLIC_SHOW_NEW_SERVICE_DESIGN === 'true';
    return isNewServiceDesignEnabled
      ? service?.serviceType?.name
      : service?.serviceType?.serviceCategory?.name;
  });

  for (const category of categoryList) {
    if (!categoryCount[category]) {
      categoryCount[category] = 1;
    } else {
      categoryCount[category] = categoryCount[category] + 1;
    }
  }
  return categoryCount;
};

// export const getCategoryInformationForAll = (
//   shops?: TTenantTypes[],
//   foodbeverage?: TTenantTypes[],
//   entertainment?: TTenantTypes[],
//   services?: IMeetingPlacePageServiceItem[],
// ): FilterTile[] => {
//   const categoryCount: CategoryCount = {};

//   const shopCategories = getCategoryInformationForTenants(shops);
//   const fbCategories = getCategoryInformationForTenants(foodbeverage);
//   const entertainmentCategories =
//     getCategoryInformationForTenants(entertainment);
//   const serviceCategories = getCategoryInformationForServices(services);

//   const merge = (input: CategoryCount): void => {
//     for (const [key, value] of Object.entries(input)) {
//       if (categoryCount[key] === undefined) {
//         categoryCount[key] = value;
//       } else {
//         categoryCount[key] += value;
//       }
//     }
//   };
//   merge(shopCategories);
//   merge(fbCategories);
//   merge(entertainmentCategories);
//   merge(serviceCategories);

//   return [
//     { categories: categoryCount, type: 'all', title: '', floorPlanIDs: [] },
//   ];
// };

export const getCategoryInformationForTenants = (
  list: TTenantTypes[] | undefined,
): CategoryCount => {
  const categoryCount: CategoryCount = {};

  if (!list) return categoryCount;

  const categoryList: string[] = list
    .map((shop) => {
      const tenantSubCategories = shop?.secondaryCategoriesCollection?.items
        ? shop.secondaryCategoriesCollection?.items
          ?.filter(Boolean)
          ?.map((c: { name: string }) => c.name)
        : [];

      const brandSubCategories = shop?.brand?.secondaryCategoriesCollection
        ?.items
        ? shop.brand.secondaryCategoriesCollection.items
          ?.filter(Boolean)
          ?.map((c: { name: string }) => c.name)
        : [];

      const primaryCategory =
        shop.primaryCategory?.name || shop.brand?.primaryCategory?.name;

      const secondaryCategories = [
        ...tenantSubCategories,
        ...brandSubCategories,
      ];

      const categories = [
        ...new Set([primaryCategory, ...secondaryCategories]),
      ];

      return categories.filter((x) => !!x);
    })
    .flat();

  for (const category of categoryList) {
    if (!categoryCount[category]) {
      categoryCount[category] = 1;
    } else {
      categoryCount[category] = categoryCount[category] + 1;
    }
  }
  return categoryCount;
};

export const isServiceTile = (tileType: FloorPlanFilterListType) => {
  return tileType === 'MeetingPlacePageService';
};

export const extractQueryParams = (searchParams: URLSearchParams) => {
  const kioskId = searchParams.get('kioskId');
  const routePreference = searchParams.get('routePreference');
  const destination = searchParams.get('destination');

  return {
    kioskId: kioskId || '',
    routePreference: routePreference || '',
    destination: destination || '',
  };
};

// export function isSvgImage(image: any): boolean {
//   if (!image) return false;

//   if (typeof image === 'string') {
//     return image.endsWith('.svg');
//   }

//   if (typeof image === 'object') {
//     return (
//       image.file?.contentType === 'image/svg+xml' ||
//       image.file?.url?.endsWith('.svg')
//     );
//   }

//   return false;
// }

//  Extract URL from any image format
// export function getImageUrl(image: any): string {
//   if (!image) return '';

//   // Direct URL string
//   if (typeof image === 'string') return image;

//   // Gatsby image
//   if (image.gatsbyImageData) {
//     return getSrc(image.gatsbyImageData) || image.gatsbyImageData || '';
//   }

//   // Contentful file
//   if (image.file?.url) return image.file.url;

//   return '';
// }

